import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { leggiPreventivoAI } from "@/lib/leggiPreventivoAI";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export const giudizioScarto = (pct) => {
  if (pct == null) return { txt: "senza riferimento", cls: "bg-zinc-100 text-zinc-600" };
  if (pct <= 0) return { txt: "conveniente", cls: "bg-emerald-100 text-emerald-800" };
  if (pct <= 10) return { txt: "in linea", cls: "bg-emerald-50 text-emerald-700" };
  if (pct <= 25) return { txt: "alto", cls: "bg-amber-100 text-amber-800" };
  return { txt: "fuori mercato", cls: "bg-rose-100 text-rose-800" };
};

// Confronto riga per riga: prezzo del fornitore vs nostro costo di acquisto (voci backoffice)
export function calcolaConfronto(righe, voci) {
  const vm = Object.fromEntries(voci.map((v) => [v.id, v]));
  const out = righe.map((r) => {
    const qty = Number(r.qty) || Number(r.qty_nostra) || 1;
    const loro = r.totale != null ? Number(r.totale) : (Number(r.prezzo_unit) || 0) * qty;
    const v = vm[r.voce_id];
    const nostro = v ? (Number(v.prezzo_acquisto) || 0) * (Number(r.qty_nostra) || qty) : null;
    const pct = nostro ? ((loro - nostro) / nostro) * 100 : null;
    return { ...r, loro_totale: loro, voce_nome: v?.name || null, nostro_costo: nostro, scarto_pct: pct };
  });
  const loro = out.reduce((s, r) => s + (r.loro_totale || 0), 0);
  const conRif = out.filter((r) => r.nostro_costo);
  const nostro = conRif.reduce((s, r) => s + r.nostro_costo, 0);
  const loroConRif = conRif.reduce((s, r) => s + (r.loro_totale || 0), 0);
  return { righe: out, totale_loro: loro, totale_nostro: nostro, scarto_pct: nostro ? ((loroConRif - nostro) / nostro) * 100 : null };
}

export function LetturaAI({ onLetto }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(0);
  const go = async (f) => {
    setBusy(true); setProg(0);
    try { const r = await leggiPreventivoAI(f, setProg); toast.success(`${(r.righe || []).length} righe lette`); onLetto(r, f); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className="border-2 border-dashed border-emerald-300 bg-emerald-50/40 rounded p-3 flex items-center gap-3" data-testid="art-ai-box">
      <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) go(f); }} data-testid="art-ai-file" />
      <Button size="sm" onClick={() => ref.current?.click()} disabled={busy} style={{ background: "var(--brand)", color: "white" }} data-testid="art-ai-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}{busy ? (prog < 100 ? `Caricamento ${prog}%` : "L'AI sta leggendo…") : "Carica PDF/foto e fai leggere all'AI"}
      </Button>
      <span className="text-[11px] text-zinc-600">Legge le voci del preventivo dell'artigiano/professionista/fornitore e le confronta con i nostri costi.</span>
    </div>
  );
}

export function ConfrontoRighe({ conf, voci, onVoce }) {
  const g = giudizioScarto(conf.scarto_pct);
  return (
    <div className="border border-zinc-200 rounded overflow-hidden" data-testid="art-confronto">
      <div className="overflow-x-auto max-h-80">
        <table className="w-full text-xs min-w-[720px]">
          <thead className="bg-zinc-50 text-[10px] uppercase text-zinc-500 sticky top-0"><tr>
            <th className="px-2 py-1.5 text-left">Voce del preventivo</th><th className="px-2 py-1.5 text-right">Loro €</th>
            <th className="px-2 py-1.5 text-left">Nostra voce</th><th className="px-2 py-1.5 text-right">Nostro costo €</th><th className="px-2 py-1.5 text-left">Esito</th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-100">
            {conf.righe.map((r, i) => { const gg = giudizioScarto(r.scarto_pct); return (
              <tr key={i} data-testid={`art-riga-${i}`}>
                <td className="px-2 py-1.5"><div className="font-medium">{r.descrizione}</div><div className="text-zinc-500 mono">{r.qty ?? "—"} {r.unit || ""} × {r.prezzo_unit != null ? eur(r.prezzo_unit) : "—"}</div></td>
                <td className="px-2 py-1.5 text-right mono">{eur(r.loro_totale)}</td>
                <td className="px-2 py-1.5">{onVoce ? (
                  <select value={r.voce_id || ""} onChange={(e) => onVoce(i, e.target.value || null)} className="h-7 w-56 border border-zinc-300 rounded text-xs bg-white" data-testid={`art-riga-voce-${i}`}>
                    <option value="">— nessuna voce —</option>
                    {voci.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>) : (r.voce_nome || "—")}</td>
                <td className="px-2 py-1.5 text-right mono">{r.nostro_costo != null ? eur(r.nostro_costo) : "—"}</td>
                <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded ${gg.cls}`}>{gg.txt}{r.scarto_pct != null ? ` ${r.scarto_pct > 0 ? "+" : ""}${r.scarto_pct.toFixed(0)}%` : ""}</span></td>
              </tr>); })}
          </tbody>
          <tfoot className="bg-zinc-50 font-semibold"><tr>
            <td className="px-2 py-2 text-right">Totale preventivo</td><td className="px-2 py-2 text-right mono" data-testid="art-tot-loro">{eur(conf.totale_loro)}</td>
            <td className="px-2 py-2 text-right">Nostro costo (voci abbinate)</td><td className="px-2 py-2 text-right mono" data-testid="art-tot-nostro">{eur(conf.totale_nostro)}</td>
            <td className="px-2 py-2"><span className={`px-1.5 py-0.5 rounded ${g.cls}`} data-testid="art-tot-esito">{g.txt}{conf.scarto_pct != null ? ` ${conf.scarto_pct > 0 ? "+" : ""}${conf.scarto_pct.toFixed(0)}%` : ""}</span></td>
          </tr></tfoot>
        </table>
      </div>
    </div>
  );
}
